import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generate complete Preview Report PDF with all sections
 * This function mirrors the exact logic from PreviewReport.js handleDownloadPDF
 */
export const generateCompletePreviewReportPDF = (reportData, isObservationVisible = false) => {
  const {
    labDetails = [],
    discipline = [],
    scope = [],
    organisationDetails = [],
    equipmentDetails = [],
    referenceMaterials = [],
    observations = [],
    proficiencyTestingProviders = [],
    nonAnalyticalInstruments = [],
    form153Phantoms = [],
    form153KitsRadioisotopes = []
  } = reportData;

  // Initialize jsPDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  let yPosition = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  // Helper function to add page if needed
  const checkAndAddPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Add header with logo and title
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('National Accreditation Board for Testing and', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 7;
  doc.text('Calibration Laboratories', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;
  doc.setFontSize(14);
  doc.text('Preview Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Lab Details Section
  const labDetail = labDetails.length > 0 ? labDetails[0] : {};

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Name of Laboratory: ${labDetail.CabName || '-'}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Contact Person: ${labDetail.NablContPerson || '-'}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Mobile: ${labDetail.ContactPersonMobileNo || '-'}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Email: ${labDetail.ContactPersonEmail1 || '-'}`, margin, yPosition);
  yPosition += 10;

  // Helper function to get observations data for a section
  const getObservationsData = (sectionName) => {
    if (!isObservationVisible) return { text: '-', isAdequate: null };

    const relevantObservations = observations.filter(
      (item) => item.SectionName?.toLowerCase() === sectionName.toLowerCase()
    );

    if (relevantObservations.length === 0) return { text: '-', isAdequate: null };

    const obsData = relevantObservations[0];
    const observation = obsData.Observation || '';
    const status = obsData.InformationStatus;
    const informationProvided = obsData.InformationProvided;

    const isAdequate = observation.trim().toLowerCase() === "document adequacy check completed" &&
      status && status.toLowerCase() === "adequate";

    if (isAdequate) {
      return { text: 'Adequate', isAdequate: true, status };
    }

    if (status && status.toLowerCase() === "inadequate") {
      const text = informationProvided || observation || 'No information provided';
      return { text, isAdequate: false, status };
    }

    return { text: observation || '-', isAdequate: null, status };
  };

  // Helper function to build table row
  const buildRow = (...values) => {
    return isObservationVisible ? [...values, ''] : values;
  };

  // Helper function to create table options with observations
  const createTableWithObservations = (sectionName, bodyData) => {
    if (!isObservationVisible) {
      return {};
    }

    return {
      didParseCell: function (data) {
        if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
          data.cell.rowSpan = bodyData.length;
        } else if (data.section === 'body' && data.column.index === 2 && data.row.index > 0) {
          data.cell.styles.fillColor = false;
          data.cell.text = [];
        }
      },
      didDrawCell: function (data) {
        if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
          const obsData = getObservationsData(sectionName);
          if (obsData.isAdequate === true) {
            doc.setTextColor(0, 128, 0); // Green for adequate
          } else if (obsData.isAdequate === false) {
            doc.setTextColor(255, 0, 0); // Red for inadequate
          } else {
            doc.setTextColor(0, 0, 0); // Black for all other observations
          }

          const cellText = obsData.text;
          const lines = doc.splitTextToSize(cellText, data.cell.width - 4);
          doc.text(lines, data.cell.x + 2, data.cell.y + 4);
          doc.setTextColor(0, 0, 0); // Reset color
        }
      }
    };
  };

  // Section: Lab General Details
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Lab General Details', margin + 2, yPosition + 5);
  yPosition += 12;

  // Lab Details Table - Complete with all fields
  const labDetailsData = [
    buildRow('Name of the CAB', labDetail.CabName || '-'),
    buildRow('Type of CAB', labDetail.CabType === 'Diagnostic' ? 'Medical Testing Laboratory' :
      labDetail.CabType === 'Medical Imaging' ? 'Medical Imaging' :
        labDetail.CabType === 'Both' ? 'Medical Testing Laboratory + Medical Imaging' : '-'),
    buildRow('GSTIN', labDetail.GSTIN || '-'),
    buildRow('PAN/TAN No.', labDetail.PANTANNumber || '-'),
    buildRow('Address', labDetail.Address || '-'),
    buildRow('Country', labDetail.CountryName || '-'),
    buildRow('State/Province', labDetail.StateName || '-'),
    buildRow('City', labDetail.CityName || '-'),
    buildRow('District', labDetail.DistrictName || '-'),
    buildRow('Sub District', labDetail.SubDistrictName || '-'),
    buildRow('Village/Town', labDetail.VillageTown || '-'),
    buildRow('Pin Code', labDetail.Pincode || '-'),
    buildRow('Mobile No', labDetail.MobileNo || '-'),
    buildRow('Landline No', labDetail.LandlineNo || '-'),
    buildRow('Email', labDetail.Email || '-'),
    buildRow('Are you from SEZ region', labDetail.IsFromSEZ ? 'Yes' : 'No'),
  ];

  if (labDetail.IsFromSEZ) {
    labDetailsData.push(buildRow('SEZ Certificate', labDetail.SEZCertificateFile || '-'));
  }

  labDetailsData.push(buildRow('Whether any individual or organization has provided consultancy for preparing towards NABL Accreditation', labDetail.HasConsultancy ? 'Yes' : 'No'));

  if (labDetail.HasConsultancy) {
    labDetailsData.push(buildRow('Name of Individual/Organization with city', labDetail.ConsultancyName || '-'));
    labDetailsData.push(buildRow('Registered Office Address', labDetail.ConsultancyAddress || '-'));
  }

  labDetailsData.push(buildRow('Has adverse action been taken against the laboratory in the past?', labDetail.HasAdverseAction ? 'Yes' : 'No'));

  if (labDetail.HasAdverseAction === "Yes") {
    labDetailsData.push(buildRow('Type of adverse action', labDetail.AdverseActionType || '-'));
    labDetailsData.push(buildRow('Adverse Decision Date', formatDate(labDetail.AdverseDecisionDate) || '-'));
    labDetailsData.push(buildRow('Previous Lab Id from NABL', labDetail.PreviousLabId || '-'));
  }

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: labDetailsData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Laboratory Details', labDetailsData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Legal Identity Details Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Legal Identity Details', margin, yPosition);
  yPosition += 8;

  const legalIdentityData = [
    buildRow('Legal Identity Type', labDetail.LegalIdentityType || '-'),
  ];

  // Add data based on legal identity type
  if (labDetail.LegalIdentityType === 'Proprietorship') {
    legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
    legalIdentityData.push(buildRow('Name of the Owner', labDetail.OwnerName || '-'));
  } else if (labDetail.LegalIdentityType === 'LLP') {
    legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
  } else if (labDetail.LegalIdentityType === 'Companies Act') {
    legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
    legalIdentityData.push(buildRow('Address', labDetail.LegalIdentity_Address || '-'));
  }

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: legalIdentityData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Legal Entity Type', legalIdentityData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Type Of Functioning Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Type Of Functioning', margin, yPosition);
  yPosition += 8;

  const functioningData = [
    buildRow('Type Of Functioning', labDetail.TypeOfFunctioning || '-'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: functioningData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Type Of Functioning', functioningData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Contact Person for NABL Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Contact Person for NABL', margin, yPosition);
  yPosition += 8;

  const contactPersonData = [
    buildRow('Contact Person for NABL', labDetail.NablContPerson || '-'),
    buildRow('Designation', labDetail.SpocDesig || '-'),
    buildRow('Mobile No', labDetail.ContactPersonMobileNo || '-'),
    buildRow('Email 1', labDetail.ContactPersonEmail1 || '-'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: contactPersonData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Contact Person from laboratory for NABL', contactPersonData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Category of Facility Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Category of Facility(s) Applied Under the Scope of Accreditation', margin, yPosition);
  yPosition += 8;

  const categoryData = [
    buildRow('Permanant Facility', labDetail.PermanentFacility ? 'Yes' : 'No'),
    buildRow('Site Facility', labDetail.SiteFacility ? 'Yes' : 'No'),
    buildRow('POCT (Point of Care Testing)', labDetail.POCTFacility ? 'Yes' : 'No'),
    ...(labDetail.CabType !== 'Medical Imaging'
      ? [buildRow('Category of which accreditation is being sought', labDetail.AccreditationCategory || '-')]
      : [])
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: categoryData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Category of Facility(s) Applied Under the Scope of Accreditation', categoryData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Parent Organisation Details Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Parent Organisation Details', margin, yPosition);
  yPosition += 8;

  const parentOrgData = [
    buildRow('Name of the Parent Organization Applicable?', labDetail.IsParentOrgApplicable ? 'Yes' : 'No'),
  ];

  if (labDetail.IsParentOrgApplicable) {
    parentOrgData.push(buildRow('Name of the Parent Organization', labDetail.ParentOrgName || '-'));
  }

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: parentOrgData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Parent Organisation Details', parentOrgData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Collection of Sample From Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Collection of Sample From', margin, yPosition);
  yPosition += 8;

  const sampleCollectionData = [
    buildRow('Sample Collection Centre(s)/ Facility(ies)', labDetail.SampleColletion ? 'Yes' : 'No'),
    buildRow('Home collection', labDetail.HomeColletion ? 'Yes' : 'No'),
    buildRow('Other Source(s)', labDetail.OtherSource ? 'Yes' : 'No'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: sampleCollectionData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Collection of Sample From', sampleCollectionData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Internal Audit and Management Review Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Internal Audit and Management Review', margin, yPosition);
  yPosition += 8;

  const auditData = [
    buildRow('Start Date of Last Internal Audit', formatDate(labDetail.InternalAuditFromDate) || '-'),
    buildRow('End Date of Last Internal Audit', formatDate(labDetail.InternalAuditToDate) || '-'),
    buildRow('Start Date of Last Management Review', formatDate(labDetail.ManagementReviewFromDate) || '-'),
    buildRow('End Date of Last Management Review', formatDate(labDetail.ManagementReviewToDate) || '-'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: auditData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Internal Audit and Management Review', auditData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Disciplines Section
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Disciplines', margin + 2, yPosition + 5);
  yPosition += 12;

  if (discipline.length > 0) {
    const disciplineData = discipline.map((disc, index) => [
      index + 1,
      disc.DisciplineName || '-'
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Sr No.', 'Discipline Name']],
      body: disciplineData,
      theme: 'grid',
      headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2 },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Scope Section - Horizontal format with merged columns
  if (scope.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Proposed Scope', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table with merged columns (matching PreviewReport.js format)
    const scopeTableData = scope.map((scopeItem, index) => {
      const srNo = scopeItem.SrNo || (index + 1); // ✅ MAIN FIX
      const scopeDetails = `Test Code: ${scopeItem.TestCode || scopeItem.testCode || '-'}\nMaterials/Products: ${scopeItem.GroupTested || '-'}\nSpecific Test: ${scopeItem.TypeOfTest || '-'}\nTest Method: ${scopeItem.ScopeStandard || '-'}`;

      const rangeDetails = `Type: ${scopeItem.rangeType || '-'}\nFacility: ${scopeItem.testPerformedAt || '-'}\nRange: ${scopeItem.RangeLL || '-'} ${scopeItem.RangeLLUnit || '-'} To ${scopeItem.RangeUL || '-'} ${scopeItem.RangeULUnit || '-'}\n%CV/MU: ${scopeItem.cv ? `CV: ${scopeItem.cv}` : ''}${scopeItem.mu ? ` MU: ${scopeItem.mu}` : ''}${!scopeItem.cv && !scopeItem.mu ? '-' : ''}${scopeItem.rangeRemark ? `\nRemarks: ${scopeItem.rangeRemark}` : ''}`;

      const equipmentDetails = `Equipment: ${scopeItem.Equipment || '-'}\nReference Material: ${scopeItem.RefName || '-'}\nProficiency Testing: ${scopeItem.PtName || '-'}`;

      if (isObservationVisible) {
        // Get observation for this scope item
        const scopeObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "scope" && obs.sourceid === scopeItem.Id
        );
        const observationText = scopeObservations.length > 0 ?
          (scopeObservations[0].ObservationRemarks || scopeObservations[0].Remarks || scopeObservations[0].Observation || '-') : '-';

        return [
          srNo, // ✅ ADD THIS
          scopeItem.DisciplineName || '-',
          scopeDetails,
          rangeDetails,
          equipmentDetails,
          observationText
        ];
      } else {
        return [
          srNo, // ✅ ADD THIS
          scopeItem.DisciplineName || '-',
          scopeDetails,
          rangeDetails,
          equipmentDetails
        ];
      }
    });

    const tableHeaders = isObservationVisible
      ? ['Sr No.', 'Discipline', 'Scope Details', 'Range Details', 'Equipment Details', 'Observations']
      : ['Sr No.', 'Discipline', 'Scope Details', 'Range Details', 'Equipment Details'];

    autoTable(doc, {
      startY: yPosition,
      head: [tableHeaders],
      body: scopeTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 12, halign: 'left' }, // Sr No
        1: { cellWidth: 25, halign: 'left' },
        2: { cellWidth: 40, halign: 'left' },
        3: { cellWidth: 40, halign: 'left' },
        4: { cellWidth: 40, halign: 'left' },
        5: { cellWidth: 25, halign: 'left' }
      } : {
        0: { cellWidth: 12, halign: 'left' }, // Sr No
        1: { cellWidth: 30, halign: 'left' },
        2: { cellWidth: 50, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' },
        4: { cellWidth: 45, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Organization Details (Staff) Section - Horizontal format
  if (organisationDetails.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Organisation Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table for organization details (matching PreviewReport.js format)
    const organizationTableData = organisationDetails.map(org => {
      const personalInfo = `Name: ${org.Name || '-'}\nDesignation: ${org.Designation || '-'}\nQualification: ${org.Qualification || '-'}\nSpecialization: ${org.Specialization || '-'}\nExperience: ${org.RelevantExperience || org.Experience || '-'} years\nTraining: ${org.RelevantTraining || '-'}`;

      const contactInfo = `Landline: ${org.LandlineNo || '-'}\nMobile: ${org.ContactNo || '-'}\nEmail: ${org.EmailID || '-'}`;

      const workInfo = `Employee Type: ${org.EmployeeType || '-'}\nEngagement Type: ${org.typeofEngagement || '-'}\nTiming: ${org.Timming || '-'}\nAuthorized Signatory: ${org.IsAuthorisedSignatory ? 'Yes' : 'No'}${org.IsAuthorisedSignatory && org.SignatorySign ? '\nSignatory File: Available' : ''}`;

      if (isObservationVisible) {
        // Get observation for this organization/staff member
        const orgObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "staff" && obs.sourceid === org.Id
        );
        const observationText = orgObservations.length > 0 ?
          (orgObservations[0].ObservationRemarks || orgObservations[0].Remarks || orgObservations[0].Observation || '-') : '-';

        return [
          org.DisciplineName || '-',
          personalInfo,
          contactInfo,
          workInfo,
          observationText
        ];
      } else {
        return [
          org.DisciplineName || '-',
          personalInfo,
          contactInfo,
          workInfo
        ];
      }
    });

    const orgTableHeaders = isObservationVisible
      ? ['Discipline', 'Personal Information', 'Contact Information', 'Work Information', 'Observations']
      : ['Discipline', 'Personal Information', 'Contact Information', 'Work Information'];

    autoTable(doc, {
      startY: yPosition,
      head: [orgTableHeaders],
      body: organizationTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 25, halign: 'left' },
        1: { cellWidth: 50, halign: 'left' },
        2: { cellWidth: 40, halign: 'left' },
        3: { cellWidth: 45, halign: 'left' },
        4: { cellWidth: 30, halign: 'left' }
      } : {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 60, halign: 'left' },
        2: { cellWidth: 50, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Equipment Details Section - Horizontal format
  if (equipmentDetails.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Equipment Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table data (matching PreviewReport.js format)
    const equipmentTableData = equipmentDetails.map(equipment => {
      const equipmentInfo = `Equipment Name: ${equipment.NameOfEquipment || '-'}\nEquipment ID: ${equipment.Equipmentid || equipment.EquipmentId || '-'}\nUID: ${equipment.UID || equipment.Uid || '-'}\nModel: ${equipment.Model || '-'}\nSerial No: ${equipment.SerialNumber || equipment.SerialNo || '-'}`;

      const technicalInfo = `Manufacturer: ${equipment.Make || equipment.MakeCompany || equipment.ManufacturerName || '-'}\nYear of Make: ${equipment.YearOfMake || equipment.ManufacturingYear || '-'}\nReceipt Date: ${equipment.ReceiptDate ? formatDate(equipment.ReceiptDate) : (equipment.DatePlacedInService ? formatDate(equipment.DatePlacedInService) : '-')}\nInstallation Date: ${equipment.InstallationDate ? formatDate(equipment.InstallationDate) : (equipment.DateOfInstallation ? formatDate(equipment.DateOfInstallation) : '-')}\nLimit of Detection: ${equipment.LimitOfDetection || equipment.LOD || equipment.RangeCapacity || '-'}`;

      const calibrationInfo = `Calibration Date: ${equipment.CalibrationDate ? formatDate(equipment.CalibrationDate) : (equipment.LastCalibrationDate ? formatDate(equipment.LastCalibrationDate) : (equipment.DateOfLastCalibration ? formatDate(equipment.DateOfLastCalibration) : '-'))}\nCalibration Due: ${equipment.CalibrationDueDate ? formatDate(equipment.CalibrationDueDate) : (equipment.NextCalibrationDate ? formatDate(equipment.NextCalibrationDate) : (equipment.CalibrationDueOn ? formatDate(equipment.CalibrationDueOn) : '-'))}\nCalibrated By: ${equipment.CalibratedBy || equipment.CalibrationType || equipment.CalibrationBy || equipment.CalibratedByType || '-'}\nCalibration Agency: ${equipment.CalibrationAgency || equipment.ExternalLaboratory || equipment.CalibrationAgencyName || equipment.ExternalCalibrationAgency || equipment.InHouseCalibration || '-'}\nTraceability: ${equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || '-'}`;

      if (isObservationVisible) {
        // Get observation for this equipment item
        const equipmentObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "equipment" && obs.sourceid === `${equipment.Id}`
        );
        const observationText = equipmentObservations.length > 0 ?
          (equipmentObservations[0].ObservationRemarks || equipmentObservations[0].Remarks || equipmentObservations[0].Observation || '-') : '-';

        return [
          equipment.DisciplineName || '-',
          equipmentInfo,
          technicalInfo,
          calibrationInfo,
          observationText
        ];
      } else {
        return [
          equipment.DisciplineName || '-',
          equipmentInfo,
          technicalInfo,
          calibrationInfo
        ];
      }
    });

    const equipmentTableHeaders = isObservationVisible
      ? ['Discipline', 'Equipment Information', 'Technical Specifications', 'Calibration Information', 'Observations']
      : ['Discipline', 'Equipment Information', 'Technical Specifications', 'Calibration Information'];

    autoTable(doc, {
      startY: yPosition,
      head: [equipmentTableHeaders],
      body: equipmentTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 25, halign: 'left' },
        1: { cellWidth: 45, halign: 'left' },
        2: { cellWidth: 45, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' },
        4: { cellWidth: 25, halign: 'left' }
      } : {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 55, halign: 'left' },
        2: { cellWidth: 55, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Reference Materials Section - Horizontal format
  if (referenceMaterials.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Reference Material Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table for reference materials (matching PreviewReport.js format)
    const refMaterialTableData = referenceMaterials.map(material => {
      const materialInfo = `Name: ${material.Name || '-'}\nType: Reference Material/Strain/Culture\nDate of Expiry: ${(material.DateOfExpiry && formatDate(material.DateOfExpiry) !== "01/01/1900" ? formatDate(material.DateOfExpiry) : '-')}`;

      const sourceTraceability = `Source: ${material.Source || '-'}\nTraceability: ${material.Traceability || '-'}`;

      if (isObservationVisible) {
        // Get observation for this reference material item
        const materialObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "reference material" && obs.sourceid === material.Id
        );
        console.log('materialObservations', materialObservations);
        const observationText = materialObservations.length > 0 ?
          (materialObservations[0].ObservationRemarks || materialObservations[0].Remarks || materialObservations[0].Observation || '-') : '-';

        return [
          material.DisciplineName || '-',
          materialInfo,
          sourceTraceability,
          observationText
        ];
      } else {
        return [
          material.DisciplineName || '-',
          materialInfo,
          sourceTraceability
        ];
      }
    });

    const refMaterialHeaders = isObservationVisible
      ? ['Discipline', 'Material Information', 'Source & Traceability', 'Observations']
      : ['Discipline', 'Material Information', 'Source & Traceability'];

    autoTable(doc, {
      startY: yPosition,
      head: [refMaterialHeaders],
      body: refMaterialTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 70, halign: 'left' },
        2: { cellWidth: 60, halign: 'left' },
        3: { cellWidth: 30, halign: 'left' }
      } : {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 80, halign: 'left' },
        2: { cellWidth: 80, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Proficiency Testing Section - Matching PreviewReport.js table structure
  if (proficiencyTestingProviders.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Proficiency Testing Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create table data matching PreviewReport.js structure
    const proficiencyTableData = proficiencyTestingProviders.length > 0 ?
      proficiencyTestingProviders.map((pt) => {
        // PT Details column
        const ptDetails = `Participation In: ${pt.participationIn || '-'}\nNodal Laboratory: ${pt.nodalLaboratory || '-'}\nOther Details: ${pt.nodalLaboratoryOthers || '-'}`;

        // ILC Details column
        const ilcDetails = `ILC Details: ${pt.ilcDetails || '-'}`;

        // Remarks column
        const remarksDetails = `Remarks: ${pt.remarks || '-'}\nCreated: ${pt.CreatedOn ? formatDate(pt.CreatedOn) : '-'}`;

        if (isObservationVisible) {
          // Get observation for this proficiency testing item
          const ptObservations = observations.filter(obs =>
            obs.SectionName?.toLowerCase() === "proficiency testing" && obs.sourceid === `${pt.Id}`
          );
          console.log('ptObservations', ptObservations);
          const observationText = ptObservations.length > 0 ?
            (ptObservations[0].ObservationRemarks || ptObservations[0].Remarks || ptObservations[0].Observation || '-') : '-';

          return [
            ptDetails,
            ilcDetails,
            remarksDetails,
            observationText
          ];
        } else {
          return [
            ptDetails,
            ilcDetails,
            remarksDetails
          ];
        }
      }) :
      [isObservationVisible ? ['No proficiency testing details specified', '', '', ''] : ['No proficiency testing details specified', '', '']];

    const proficiencyHeaders = isObservationVisible
      ? ['PT Details', 'ILC Details', 'Remarks', 'Observations']
      : ['PT Details', 'ILC Details', 'Remarks'];

    autoTable(doc, {
      startY: yPosition,
      head: [proficiencyHeaders],
      body: proficiencyTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 50, halign: 'left' },
        1: { cellWidth: 50, halign: 'left' },
        2: { cellWidth: 50, halign: 'left' },
        3: { cellWidth: 40, halign: 'left' }
      } : {
        0: { cellWidth: 60, halign: 'left' },
        1: { cellWidth: 65, halign: 'left' },
        2: { cellWidth: 65, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Non Analytical Instruments Section - Horizontal format (like scope section)
  if (nonAnalyticalInstruments.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Non Analytical Instruments Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table for non-analytical instruments (similar to scope format)
    const instrumentTableData = nonAnalyticalInstruments.map(instrument => {
      const instrumentDetails = `Instrument Name: ${instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : '-'}\nUID: ${instrument.UID || '-'}\nType: ${instrument.InstrumentType || '-'}\nModel: ${instrument.Model || '-'}\nMake: ${instrument.Make || '-'}`;

      const calibrationDetails = `Calibration Frequency: ${instrument.Calibration_Frequency || '-'}\nLast Calibration Date: ${formatDate(instrument.LastCalibration_Date) || '-'}\nNext Calibration Date: ${formatDate(instrument.NextCalibration_Date) || '-'}\nCalibration Agency: ${instrument.CalibrationAgency || '-'}`;

      const statusDetails = `Status: ${instrument.Status || 'Active'}\nLocation: ${instrument.Location || '-'}\nPurchase Date: ${formatDate(instrument.PurchaseDate) || '-'}\nCondition: ${instrument.Condition || '-'}`;

      if (isObservationVisible) {
        // Get observation for this instrument item
        const instrumentObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "non-analytical instruments" && obs.sourceid === `${instrument.Id}`
        );
        const observationText = instrumentObservations.length > 0 ?
          (instrumentObservations[0].ObservationRemarks || instrumentObservations[0].Remarks || instrumentObservations[0].Observation || '-') : '-';

        return [
          instrumentDetails,
          calibrationDetails,
          statusDetails,
          observationText
        ];
      } else {
        return [
          instrumentDetails,
          calibrationDetails,
          statusDetails
        ];
      }
    });

    const instrumentHeaders = isObservationVisible
      ? ['Instrument Details', 'Calibration Details', 'Status Details', 'Observations']
      : ['Instrument Details', 'Calibration Details', 'Status Details'];

    autoTable(doc, {
      startY: yPosition,
      head: [instrumentHeaders],
      body: instrumentTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 45, halign: 'left' },
        1: { cellWidth: 45, halign: 'left' },
        2: { cellWidth: 45, halign: 'left' },
        3: { cellWidth: 35, halign: 'left' }
      } : {
        0: { cellWidth: 60, halign: 'left' },
        1: { cellWidth: 65, halign: 'left' },
        2: { cellWidth: 65, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Organization Chart Section - Matching UI table structure
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Organization Chart', margin + 2, yPosition + 5);
  yPosition += 12;

  // Create table data matching the UI structure exactly
  const orgChartData = [
    [
      'Organization Chart Document',
      labDetail.OrganizationChart ? 'View' : '-'
    ],
    [
      'Parent Organization Chart Document',
      labDetail.ParentOrganizationChart ? 'View' : '-'
    ],
    [
      'Management System Document',
      labDetail.ManagementSystemDocument ? 'View' : '-'
    ]
  ];

  const orgChartHeaders = isObservationVisible
    ? ['Document Type', 'Status', 'Observations']
    : ['Document Type', 'Status'];

  // Add observations column if needed
  if (isObservationVisible) {
    orgChartData.forEach((row, index) => {
      const orgObservations = observations.filter(obs => {
        if (index === 0) return obs.SectionName?.toLowerCase() === "organization details";
        if (index === 1) return obs.SectionName?.toLowerCase() === "organization details";
        return obs.SectionName?.toLowerCase() === "organization details";
      });
      const observationText = orgObservations.length > 0 ?
        (orgObservations[0].ObservationRemarks || orgObservations[0].Remarks || orgObservations[0].Observation || '-') : '-';
      row.push(observationText);
    });
  }

  autoTable(doc, {
    startY: yPosition,
    head: [orgChartHeaders],
    body: orgChartData,
    theme: 'grid',
    headStyles: {
      fillColor: [219, 216, 216],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center'
    },
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'top'
    },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 80, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 60, halign: 'left' },
      2: { cellWidth: 50, halign: 'left' }
    } : {
      0: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 90, halign: 'left' }
    },
    margin: { left: margin, right: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Proficiency Testing Documents Section - Matching UI table structure
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Proficiency Testing Documents', margin + 2, yPosition + 5);
  yPosition += 12;

  // Create table data matching the UI structure exactly
  const ptDocData = [
    [
      'Proficiency Testing Documents',
      labDetail.QMSFile ? 'View' : '-'
    ]
  ];

  const ptDocHeaders = isObservationVisible
    ? ['Document Type', 'Status', 'Observations']
    : ['Document Type', 'Status'];

  // Add observations column if needed
  if (isObservationVisible) {
    ptDocData.forEach(row => {
      const ptDocObservations = observations.filter(obs =>
        obs.SectionName?.toLowerCase() === "proficiency testing document"
      );
      const observationText = ptDocObservations.length > 0 ?
        (ptDocObservations[0].ObservationRemarks || ptDocObservations[0].Remarks || ptDocObservations[0].Observation || '-') : '-';
      row.push(observationText);
    });
  }

  autoTable(doc, {
    startY: yPosition,
    head: [ptDocHeaders],
    body: ptDocData,
    theme: 'grid',
    headStyles: {
      fillColor: [219, 216, 216],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center'
    },
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'top'
    },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 80, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 60, halign: 'left' },
      2: { cellWidth: 50, halign: 'left' }
    } : {
      0: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 90, halign: 'left' }
    },
    margin: { left: margin, right: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Declaration Section
  checkAndAddPage(50);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 10, 'F');
  doc.text('DECLARATION', pageWidth / 2, yPosition + 6, { align: 'center' });
  yPosition += 15;

  // Terms & Conditions Section
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Terms & Conditions', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');

  const termsText = `The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.

The Conformity Assessment Body {Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:

By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.

Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.`;

  const termsLines = doc.splitTextToSize(termsText, contentWidth);
  doc.text(termsLines, margin, yPosition);
  yPosition += termsLines.length * 4 + 10;

  // Declaration by the Laboratory Section
  checkAndAddPage(40);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Declaration by the Laboratory', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('We declare that:', margin, yPosition);
  yPosition += 6;

  const declarationPoints = [
    'We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.',
    'We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).',
    'We have declared all sources of samples.',
    'We have declared and submitted accurate and complete information in the enclosed "Declaration Form"',
    'We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.',
    'We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).',
    'We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.',
    'We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.',
    'We comply to all national, regional and local regulatory requirements for operating a laboratory.',
    'All information provided in this application is true.',
    'Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO 15189.'
  ];

  declarationPoints.forEach((point, index) => {
    checkAndAddPage(15);
    const pointText = `${index + 1}. ${point}`;
    const pointLines = doc.splitTextToSize(pointText, contentWidth - 5);
    doc.text(pointLines, margin, yPosition);
    yPosition += pointLines.length * 4 + 3;
  });

  yPosition += 10;

  // Add additional declaration text
  checkAndAddPage(20);
  const additionalText1 = `${labDetail.DeclNameOrg || 'The authorized representative of CAB'}, authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.`;
  const additionalLines1 = doc.splitTextToSize(additionalText1, contentWidth);
  doc.text(additionalLines1, margin, yPosition);
  yPosition += additionalLines1.length * 4 + 8;

  checkAndAddPage(20);
  const additionalText2 = `I have read and understood all the terms and conditions mentioned above. *`;
  const additionalLines2 = doc.splitTextToSize(additionalText2, contentWidth);
  doc.text(additionalLines2, margin, yPosition);
  yPosition += additionalLines2.length * 4 + 8;

  checkAndAddPage(30);
  const additionalText3 = `By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'. *`;
  const additionalLines3 = doc.splitTextToSize(additionalText3, contentWidth);
  doc.text(additionalLines3, margin, yPosition);
  yPosition += additionalLines3.length * 4 + 10;

  // Add footer with page numbers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth - margin,
      pageHeight - 10,
      { align: 'right' }
    );
  }

  return doc;
};


export const generateCompletePreviewReportPDFCalibration = (reportData, isObservationVisible = false) => {
  const {
    labDetails = [],
    discipline = [],
    scope = [],
    organisationDetails = [],
    equipmentDetails = [],
    referenceMaterials = [],
    observations = [],
    proficiencyTestingProviders = [],
    nonAnalyticalInstruments = [],
    form153Phantoms = [],
    form153KitsRadioisotopes = []
  } = reportData;

  // Initialize jsPDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  let yPosition = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  // Helper function to add page if needed
  const checkAndAddPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Add header with logo and title
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('National Accreditation Board for Testing and', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 7;
  doc.text('Calibration Laboratories', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;
  doc.setFontSize(14);
  doc.text('Preview Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Lab Details Section
  const labDetail = labDetails.length > 0 ? labDetails[0] : {};

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Name of Laboratory: ${labDetail.CabName || '-'}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Contact Person: ${labDetail.NablContPerson || '-'}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Mobile: ${labDetail.ContactPersonMobileNo || '-'}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Email: ${labDetail.ContactPersonEmail1 || '-'}`, margin, yPosition);
  yPosition += 10;

  // Helper function to get observations data for a section
  const getObservationsData = (sectionName) => {
    if (!isObservationVisible) return { text: '-', isAdequate: null };

    const relevantObservations = observations.filter(
      (item) => item.SectionName?.toLowerCase() === sectionName.toLowerCase()
    );

    if (relevantObservations.length === 0) return { text: '-', isAdequate: null };

    const obsData = relevantObservations[0];
    const observation = obsData.Observation || '';
    const status = obsData.InformationStatus;
    const informationProvided = obsData.InformationProvided;

    const isAdequate = observation.trim().toLowerCase() === "document adequacy check completed" &&
      status && status.toLowerCase() === "adequate";

    if (isAdequate) {
      return { text: 'Adequate', isAdequate: true, status };
    }

    if (status && status.toLowerCase() === "inadequate") {
      const text = informationProvided || observation || 'No information provided';
      return { text, isAdequate: false, status };
    }

    return { text: observation || '-', isAdequate: null, status };
  };

  // Helper function to build table row
  const buildRow = (...values) => {
    return isObservationVisible ? [...values, ''] : values;
  };

  // Helper function to create table options with observations
  const createTableWithObservations = (sectionName, bodyData) => {
    if (!isObservationVisible) {
      return {};
    }

    return {
      didParseCell: function (data) {
        if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
          data.cell.rowSpan = bodyData.length;
        } else if (data.section === 'body' && data.column.index === 2 && data.row.index > 0) {
          data.cell.styles.fillColor = false;
          data.cell.text = [];
        }
      },
      didDrawCell: function (data) {
        if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
          const obsData = getObservationsData(sectionName);
          if (obsData.isAdequate === true) {
            doc.setTextColor(0, 128, 0); // Green for adequate
          } else if (obsData.isAdequate === false) {
            doc.setTextColor(255, 0, 0); // Red for inadequate
          } else {
            doc.setTextColor(0, 0, 0); // Black for all other observations
          }

          const cellText = obsData.text;
          const lines = doc.splitTextToSize(cellText, data.cell.width - 4);
          doc.text(lines, data.cell.x + 2, data.cell.y + 4);
          doc.setTextColor(0, 0, 0); // Reset color
        }
      }
    };
  };

  // Section: Lab General Details
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Lab General Details', margin + 2, yPosition + 5);
  yPosition += 12;

  // Lab Details Table - Complete with all fields
  const labDetailsData = [
    buildRow('Name of the CAB', labDetail.CabName || '-'),
    buildRow('GSTIN', labDetail.GSTIN || '-'),
    buildRow('PAN/TAN No.', labDetail.PANTANNumber || '-'),
    buildRow('Address', labDetail.Address || '-'),
    buildRow('Country', labDetail.CountryName || '-'),
    buildRow('State/Province', labDetail.StateName || '-'),
    buildRow('City', labDetail.CityName || '-'),
    buildRow('District', labDetail.DistrictName || '-'),
    buildRow('Sub District', labDetail.SubDistrictName || '-'),
    buildRow('Village/Town', labDetail.VillageTown || '-'),
    buildRow('Pin Code', labDetail.Pincode || '-'),
    buildRow('Mobile No', labDetail.MobileNo || '-'),
    buildRow('Landline No', labDetail.LandlineNo || '-'),
    buildRow('Email', labDetail.Email || '-'),
    buildRow('Are you from SEZ region', labDetail.IsFromSEZ ? 'Yes' : 'No'),
  ];

  if (labDetail.IsFromSEZ) {
    labDetailsData.push(buildRow('SEZ Certificate', labDetail.SEZCertificateFile || '-'));
  }

  labDetailsData.push(buildRow('Whether any individual or organization has provided consultancy for preparing towards NABL Accreditation', labDetail.HasConsultancy ? 'Yes' : 'No'));

  if (labDetail.HasConsultancy) {
    labDetailsData.push(buildRow('Name of Individual/Organization with city', labDetail.ConsultancyName || '-'));
    labDetailsData.push(buildRow('Registered Office Address', labDetail.ConsultancyAddress || '-'));
  }

  labDetailsData.push(buildRow('Has adverse action been taken against the laboratory in the past?', labDetail.HasAdverseAction ? 'Yes' : 'No'));

  if (labDetail.HasAdverseAction === "Yes") {
    labDetailsData.push(buildRow('Type of adverse action', labDetail.AdverseActionType || '-'));
    labDetailsData.push(buildRow('Adverse Decision Date', formatDate(labDetail.AdverseDecisionDate) || '-'));
    labDetailsData.push(buildRow('Previous Lab Id from NABL', labDetail.PreviousLabId || '-'));
  }

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: labDetailsData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Laboratory Details', labDetailsData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Legal Identity Details Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Legal Identity Details', margin, yPosition);
  yPosition += 8;

  const legalIdentityData = [
    buildRow('Legal Identity Type', labDetail.LegalIdentityType || '-'),
  ];

  // Add data based on legal identity type
  if (labDetail.LegalIdentityType === 'Proprietorship') {
    legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
    legalIdentityData.push(buildRow('Name of the Owner', labDetail.OwnerName || '-'));
  } else if (labDetail.LegalIdentityType === 'LLP') {
    legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
  } else if (labDetail.LegalIdentityType === 'Companies Act') {
    legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
    legalIdentityData.push(buildRow('Address', labDetail.LegalIdentity_Address || '-'));
  }

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: legalIdentityData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Legal Entity Type', legalIdentityData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Type Of Functioning Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Type of service provided by the Calibration laboratory', margin, yPosition);
  yPosition += 8;

  const functioningData = [
    buildRow('Type of service provided by the Calibration laboratory', labDetail.TypeOfFunctioning || '-'),
    buildRow('Other Accreditation', labDetail.OtherAccreditation || '-'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: functioningData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Type of service provided by the Calibration laboratory', functioningData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Contact Person for NABL Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Contact Person for NABL', margin, yPosition);
  yPosition += 8;

  const contactPersonData = [
    buildRow('Contact Person for NABL', labDetail.NablContPerson || '-'),
    buildRow('Designation', labDetail.SpocDesig || '-'),
    buildRow('Mobile No', labDetail.ContactPersonMobileNo || '-'),
    buildRow('Email 1', labDetail.ContactPersonEmail1 || '-'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: contactPersonData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Contact Person from laboratory for NABL', contactPersonData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Category of Facility Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Category of Facility(s) Applied Under the Scope of Accreditation', margin, yPosition);
  yPosition += 8;

  const categoryData = [
    buildRow('Permanant Facility', labDetail.PermanentFacility ? 'Yes' : 'No'),
    buildRow('Site Facility', labDetail.SiteFacility ? 'Yes' : 'No'),
    buildRow('POCT (Point of Care Testing)', labDetail.POCTFacility ? 'Yes' : 'No'),
    ...(labDetail.CabType !== 'Medical Imaging'
      ? [buildRow('Category of which accreditation is being sought', labDetail.AccreditationCategory || '-')]
      : [])
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: categoryData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Category of Facility(s) Applied Under the Scope of Accreditation', categoryData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Parent Organisation Details Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Parent Organisation Details', margin, yPosition);
  yPosition += 8;

  const parentOrgData = [
    buildRow('Name of the Parent Organization Applicable?', labDetail.IsParentOrgApplicable ? 'Yes' : 'No'),
  ];

  if (labDetail.IsParentOrgApplicable) {
    parentOrgData.push(buildRow('Name of the Parent Organization', labDetail.ParentOrgName || '-'));
  }

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: parentOrgData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Parent Organisation Details', parentOrgData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Internal Audit and Management Review Section
  checkAndAddPage(30);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Internal Audit and Management Review', margin, yPosition);
  yPosition += 8;

  const auditData = [
    buildRow('Start Date of Last Internal Audit', formatDate(labDetail.InternalAuditFromDate) || '-'),
    buildRow('End Date of Last Internal Audit', formatDate(labDetail.InternalAuditToDate) || '-'),
    buildRow('Start Date of Last Management Review', formatDate(labDetail.ManagementReviewFromDate) || '-'),
    buildRow('End Date of Last Management Review', formatDate(labDetail.ManagementReviewToDate) || '-'),
  ];

  autoTable(doc, {
    startY: yPosition,
    head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
    body: auditData,
    theme: 'grid',
    headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
    styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 60 },
      1: { cellWidth: 60 },
      2: { cellWidth: 70 }
    } : {
      0: { cellWidth: 70 },
      1: { cellWidth: 120 }
    },
    margin: { left: margin, right: margin },
    ...createTableWithObservations('Internal Audit and Management Review', auditData)
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Disciplines Section
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Disciplines', margin + 2, yPosition + 5);
  yPosition += 12;

  if (discipline.length > 0) {
    const disciplineData = discipline.map((disc, index) => [
      index + 1,
      disc.DisciplineName ? `${disc.DisciplineName} - ${disc.groupName || '-'}` : '-'
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Sr No.', 'Discipline/Group']],
      body: disciplineData,
      theme: 'grid',
      headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2 },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Scope Section - Horizontal format with merged columns
  if (scope.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Proposed Scope', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table with merged columns (matching PreviewReport.js format)
    const scopeTableData = scope.map((scopeItem, index) => {
      const srNo = scopeItem.SrNo || (index + 1); // ✅ MAIN FIX
      const scopeDetails = `Test Code: ${scopeItem.TestCode || scopeItem.testCode || '-'}\nMaterials/Products: ${scopeItem.GroupTested || '-'}\nSpecific Test: ${scopeItem.TypeOfTest || '-'}\nTest Method: ${scopeItem.ScopeStandard || '-'}`;

      const rangeDetails = `Type: ${scopeItem.rangeType || "-"}
        Facility: ${scopeItem.testPerformedAt || "-"}
        Range: ${scopeItem.RangeLL || "-"} ${scopeItem.RangeLLUnit || "-"} To ${scopeItem.RangeUL || "-"} ${scopeItem.RangeULUnit || "-"}
        CMC: ${scopeItem.cv ? `LL: ${scopeItem.cv}${scopeItem.mu || ""}` : ""} ${
         scopeItem.cmcuppervalue
          ? `UL: ${scopeItem.cmcuppervalue}${scopeItem.cmcupperUnit || ""}`
          : ""
        } ${!scopeItem.cv && !scopeItem.cmcuppervalue ? "-" : ""}${scopeItem.rangeRemark ? `Remarks: ${scopeItem.rangeRemark}` : ""}`.trim();

      const equipmentDetails = `Equipment: ${scopeItem.Equipment || '-'}\nReference Material: ${scopeItem.RefName || '-'}\nProficiency Testing: ${scopeItem.PtName || '-'}`;

      if (isObservationVisible) {
        // Get observation for this scope item
        const scopeObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "scope" && obs.sourceid === scopeItem.Id
        );
        const observationText = scopeObservations.length > 0 ?
          (scopeObservations[0].ObservationRemarks || scopeObservations[0].Remarks || scopeObservations[0].Observation || '-') : '-';

        return [
          srNo, // ✅ ADD THIS
          scopeItem.DisciplineName +'-'+scopeItem.groupName || '-',
          scopeDetails,
          rangeDetails,
          equipmentDetails,
          observationText
        ];
      } else {
        return [
          srNo, // ✅ ADD THIS
          scopeItem.DisciplineName +'-'+scopeItem.groupName || '-',
          scopeDetails,
          rangeDetails,
          equipmentDetails
        ];
      }
    });

    const tableHeaders = isObservationVisible
      ? ['Sr No.', 'Discipline/Group', 'Scope Details', 'Range Details', 'Equipment Details', 'Observations']
      : ['Sr No.', 'Discipline/Group', 'Scope Details', 'Range Details', 'Equipment Details'];

    autoTable(doc, {
      startY: yPosition,
      head: [tableHeaders],
      body: scopeTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 12, halign: 'left' }, // Sr No
        1: { cellWidth: 25, halign: 'left' },
        2: { cellWidth: 40, halign: 'left' },
        3: { cellWidth: 40, halign: 'left' },
        4: { cellWidth: 40, halign: 'left' },
        5: { cellWidth: 25, halign: 'left' }
      } : {
        0: { cellWidth: 12, halign: 'left' }, // Sr No
        1: { cellWidth: 30, halign: 'left' },
        2: { cellWidth: 50, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' },
        4: { cellWidth: 45, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Organization Details (Staff) Section - Horizontal format
  if (organisationDetails.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Organisation Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table for organization details (matching PreviewReport.js format)
    const organizationTableData = organisationDetails.map(org => {
      const personalInfo = `Name: ${org.Name || '-'}\nDesignation: ${org.Designation || '-'}\nQualification: ${org.Qualification || '-'}\nSpecialization: ${org.Specialization || '-'}\nExperience: ${org.RelevantExperience || org.Experience || '-'} years\nTraining: ${org.RelevantTraining || '-'}`;

      const contactInfo = `Landline: ${org.LandlineNo || '-'}\nMobile: ${org.ContactNo || '-'}\nEmail: ${org.EmailID || '-'}`;

      const workInfo = `Employee Type: ${org.EmployeeType || '-'}\nEngagement Type: ${org.typeofEngagement || '-'}\nTiming: ${org.Timming || '-'}\nAuthorized Signatory: ${org.IsAuthorisedSignatory ? 'Yes' : 'No'}${org.IsAuthorisedSignatory && org.SignatorySign ? '\nSignatory File: Available' : ''}`;

      if (isObservationVisible) {
        // Get observation for this organization/staff member
        const orgObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "staff" && obs.sourceid === org.Id
        );
        const observationText = orgObservations.length > 0 ?
          (orgObservations[0].ObservationRemarks || orgObservations[0].Remarks || orgObservations[0].Observation || '-') : '-';

        return [
          org.DisciplineName +'-'+org.GroupName || '-',
          personalInfo,
          contactInfo,
          workInfo,
          observationText
        ];
      } else {
        return [
          org.DisciplineName +'-'+org.GroupName || '-',
          personalInfo,
          contactInfo,
          workInfo
        ];
      }
    });

    const orgTableHeaders = isObservationVisible
      ? ['Discipline/Group', 'Personal Information', 'Contact Information', 'Work Information', 'Observations']
      : ['Discipline/Group', 'Personal Information', 'Contact Information', 'Work Information'];

    autoTable(doc, {
      startY: yPosition,
      head: [orgTableHeaders],
      body: organizationTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 25, halign: 'left' },
        1: { cellWidth: 50, halign: 'left' },
        2: { cellWidth: 40, halign: 'left' },
        3: { cellWidth: 45, halign: 'left' },
        4: { cellWidth: 30, halign: 'left' }
      } : {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 60, halign: 'left' },
        2: { cellWidth: 50, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Equipment Details Section - Horizontal format
  if (equipmentDetails.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Equipment Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table data (matching PreviewReport.js format)
    const equipmentTableData = equipmentDetails.map(equipment => {
      const equipmentInfo = `Equipment Name: ${equipment.NameOfEquipment || '-'}\nEquipment ID: ${equipment.Equipmentid || equipment.EquipmentId || '-'}\nUID: ${equipment.UID || equipment.Uid || '-'}\nModel: ${equipment.Model || '-'}\nSerial No: ${equipment.SerialNumber || equipment.SerialNo || '-'}`;

      const technicalInfo = `Manufacturer: ${equipment.Make || equipment.MakeCompany || equipment.ManufacturerName || '-'}\nYear of Make: ${equipment.YearOfMake || equipment.ManufacturingYear || '-'}\nReceipt Date: ${equipment.ReceiptDate ? formatDate(equipment.ReceiptDate) : (equipment.DatePlacedInService ? formatDate(equipment.DatePlacedInService) : '-')}\nInstallation Date: ${equipment.InstallationDate ? formatDate(equipment.InstallationDate) : (equipment.DateOfInstallation ? formatDate(equipment.DateOfInstallation) : '-')}\nLimit of Detection: ${equipment.LimitOfDetection || equipment.LOD || equipment.RangeCapacity || '-'}`;

      const calibrationInfo = `Calibration Date: ${equipment.CalibrationDate ? formatDate(equipment.CalibrationDate) : (equipment.LastCalibrationDate ? formatDate(equipment.LastCalibrationDate) : (equipment.DateOfLastCalibration ? formatDate(equipment.DateOfLastCalibration) : '-'))}\nCalibration Due: ${equipment.CalibrationDueDate ? formatDate(equipment.CalibrationDueDate) : (equipment.NextCalibrationDate ? formatDate(equipment.NextCalibrationDate) : (equipment.CalibrationDueOn ? formatDate(equipment.CalibrationDueOn) : '-'))}\nCalibrated By: ${equipment.CalibratedBy || equipment.CalibrationType || equipment.CalibrationBy || equipment.CalibratedByType || '-'}\nCalibration Agency: ${equipment.CalibrationAgency || equipment.ExternalLaboratory || equipment.CalibrationAgencyName || equipment.ExternalCalibrationAgency || equipment.InHouseCalibration || '-'}\nTraceability: ${equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || '-'}`;

      if (isObservationVisible) {
        // Get observation for this equipment item
        const equipmentObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "equipment" && obs.sourceid === `${equipment.Id}`
        );
        const observationText = equipmentObservations.length > 0 ?
          (equipmentObservations[0].ObservationRemarks || equipmentObservations[0].Remarks || equipmentObservations[0].Observation || '-') : '-';

        return [
          equipment.DisciplineName + '-' + equipment.GroupName || '-',
          equipmentInfo,
          technicalInfo,
          calibrationInfo,
          observationText
        ];
      } else {
        return [
          equipment.DisciplineName + '-' + equipment.GroupName || '-',
          equipmentInfo,
          technicalInfo,
          calibrationInfo
        ];
      }
    });

    const equipmentTableHeaders = isObservationVisible
      ? ['Discipline/Group', 'Equipment Information', 'Technical Specifications', 'Calibration Information', 'Observations']
      : ['Discipline/Group', 'Equipment Information', 'Technical Specifications', 'Calibration Information'];

    autoTable(doc, {
      startY: yPosition,
      head: [equipmentTableHeaders],
      body: equipmentTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 25, halign: 'left' },
        1: { cellWidth: 45, halign: 'left' },
        2: { cellWidth: 45, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' },
        4: { cellWidth: 25, halign: 'left' }
      } : {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 55, halign: 'left' },
        2: { cellWidth: 55, halign: 'left' },
        3: { cellWidth: 50, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Reference Materials Section - Horizontal format
  if (referenceMaterials.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Reference Material Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table for reference materials (matching PreviewReport.js format)
    const refMaterialTableData = referenceMaterials.map(material => {
      const materialInfo = `Name: ${material.Name || '-'}\nType: Reference Material/Strain/Culture\nDate of Expiry: ${(material.DateOfExpiry && formatDate(material.DateOfExpiry) !== "01/01/1900" ? formatDate(material.DateOfExpiry) : '-')}`;

      const sourceTraceability = `Source: ${material.Source || '-'}\nTraceability: ${material.Traceability || '-'}`;

      if (isObservationVisible) {
        // Get observation for this reference material item
        const materialObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "reference material" && obs.sourceid === material.Id
        );
        console.log('materialObservations', materialObservations);
        const observationText = materialObservations.length > 0 ?
          (materialObservations[0].ObservationRemarks || materialObservations[0].Remarks || materialObservations[0].Observation || '-') : '-';

        return [
          material.DisciplineName || '-',
          materialInfo,
          sourceTraceability,
          observationText
        ];
      } else {
        return [
          material.DisciplineName || '-',
          materialInfo,
          sourceTraceability
        ];
      }
    });

    const refMaterialHeaders = isObservationVisible
      ? ['Discipline', 'Material Information', 'Source & Traceability', 'Observations']
      : ['Discipline', 'Material Information', 'Source & Traceability'];

    autoTable(doc, {
      startY: yPosition,
      head: [refMaterialHeaders],
      body: refMaterialTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 70, halign: 'left' },
        2: { cellWidth: 60, halign: 'left' },
        3: { cellWidth: 30, halign: 'left' }
      } : {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 80, halign: 'left' },
        2: { cellWidth: 80, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Proficiency Testing Section - Matching PreviewReport.js table structure
  if (proficiencyTestingProviders.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Proficiency Testing Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create table data matching PreviewReport.js structure
    const proficiencyTableData = proficiencyTestingProviders.length > 0 ?
      proficiencyTestingProviders.map((pt) => {
        // PT Details column
        const ptDetails = `Participation In: ${pt.participationIn || '-'}\nNodal Laboratory: ${pt.nodalLaboratory || '-'}\nOther Details: ${pt.nodalLaboratoryOthers || '-'}`;

        // ILC Details column
        const ilcDetails = `ILC Details: ${pt.ilcDetails || '-'}`;

        // Remarks column
        const remarksDetails = `Remarks: ${pt.remarks || '-'}\nCreated: ${pt.CreatedOn ? formatDate(pt.CreatedOn) : '-'}`;

        if (isObservationVisible) {
          // Get observation for this proficiency testing item
          const ptObservations = observations.filter(obs =>
            obs.SectionName?.toLowerCase() === "proficiency testing" && obs.sourceid === `${pt.Id}`
          );
          console.log('ptObservations', ptObservations);
          const observationText = ptObservations.length > 0 ?
            (ptObservations[0].ObservationRemarks || ptObservations[0].Remarks || ptObservations[0].Observation || '-') : '-';

          return [
            ptDetails,
            ilcDetails,
            remarksDetails,
            observationText
          ];
        } else {
          return [
            ptDetails,
            ilcDetails,
            remarksDetails
          ];
        }
      }) :
      [isObservationVisible ? ['No proficiency testing details specified', '', '', ''] : ['No proficiency testing details specified', '', '']];

    const proficiencyHeaders = isObservationVisible
      ? ['PT Details', 'ILC Details', 'Remarks', 'Observations']
      : ['PT Details', 'ILC Details', 'Remarks'];

    autoTable(doc, {
      startY: yPosition,
      head: [proficiencyHeaders],
      body: proficiencyTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 50, halign: 'left' },
        1: { cellWidth: 50, halign: 'left' },
        2: { cellWidth: 50, halign: 'left' },
        3: { cellWidth: 40, halign: 'left' }
      } : {
        0: { cellWidth: 60, halign: 'left' },
        1: { cellWidth: 65, halign: 'left' },
        2: { cellWidth: 65, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Non Analytical Instruments Section - Horizontal format (like scope section)
  if (nonAnalyticalInstruments.length > 0) {
    checkAndAddPage(30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    doc.text('Non Analytical Instruments Details', margin + 2, yPosition + 5);
    yPosition += 12;

    // Create horizontal table for non-analytical instruments (similar to scope format)
    const instrumentTableData = nonAnalyticalInstruments.map(instrument => {
      const instrumentDetails = `Instrument Name: ${instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : '-'}\nUID: ${instrument.UID || '-'}\nType: ${instrument.InstrumentType || '-'}\nModel: ${instrument.Model || '-'}\nMake: ${instrument.Make || '-'}`;

      const calibrationDetails = `Calibration Frequency: ${instrument.Calibration_Frequency || '-'}\nLast Calibration Date: ${formatDate(instrument.LastCalibration_Date) || '-'}\nNext Calibration Date: ${formatDate(instrument.NextCalibration_Date) || '-'}\nCalibration Agency: ${instrument.CalibrationAgency || '-'}`;

      const statusDetails = `Status: ${instrument.Status || 'Active'}\nLocation: ${instrument.Location || '-'}\nPurchase Date: ${formatDate(instrument.PurchaseDate) || '-'}\nCondition: ${instrument.Condition || '-'}`;

      if (isObservationVisible) {
        // Get observation for this instrument item
        const instrumentObservations = observations.filter(obs =>
          obs.SectionName?.toLowerCase() === "non-analytical instruments" && obs.sourceid === `${instrument.Id}`
        );
        const observationText = instrumentObservations.length > 0 ?
          (instrumentObservations[0].ObservationRemarks || instrumentObservations[0].Remarks || instrumentObservations[0].Observation || '-') : '-';

        return [
          instrumentDetails,
          calibrationDetails,
          statusDetails,
          observationText
        ];
      } else {
        return [
          instrumentDetails,
          calibrationDetails,
          statusDetails
        ];
      }
    });

    const instrumentHeaders = isObservationVisible
      ? ['Instrument Details', 'Calibration Details', 'Status Details', 'Observations']
      : ['Instrument Details', 'Calibration Details', 'Status Details'];

    autoTable(doc, {
      startY: yPosition,
      head: [instrumentHeaders],
      body: instrumentTableData,
      theme: 'grid',
      headStyles: {
        fillColor: [219, 216, 216],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      styles: {
        fontSize: 6,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'top'
      },
      columnStyles: isObservationVisible ? {
        0: { cellWidth: 45, halign: 'left' },
        1: { cellWidth: 45, halign: 'left' },
        2: { cellWidth: 45, halign: 'left' },
        3: { cellWidth: 35, halign: 'left' }
      } : {
        0: { cellWidth: 60, halign: 'left' },
        1: { cellWidth: 65, halign: 'left' },
        2: { cellWidth: 65, halign: 'left' }
      },
      margin: { left: margin, right: margin },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Organization Chart Section - Matching UI table structure
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Organization Chart', margin + 2, yPosition + 5);
  yPosition += 12;

  // Create table data matching the UI structure exactly
  const orgChartData = [
    [
      'Organization Chart Document',
      labDetail.OrganizationChart ? 'View' : '-'
    ],
    [
      'Parent Organization Chart Document',
      labDetail.ParentOrganizationChart ? 'View' : '-'
    ],
    [
      'Management System Document',
      labDetail.ManagementSystemDocument ? 'View' : '-'
    ]
  ];

  const orgChartHeaders = isObservationVisible
    ? ['Document Type', 'Status', 'Observations']
    : ['Document Type', 'Status'];

  // Add observations column if needed
  if (isObservationVisible) {
    orgChartData.forEach((row, index) => {
      const orgObservations = observations.filter(obs => {
        if (index === 0) return obs.SectionName?.toLowerCase() === "organization details";
        if (index === 1) return obs.SectionName?.toLowerCase() === "organization details";
        return obs.SectionName?.toLowerCase() === "organization details";
      });
      const observationText = orgObservations.length > 0 ?
        (orgObservations[0].ObservationRemarks || orgObservations[0].Remarks || orgObservations[0].Observation || '-') : '-';
      row.push(observationText);
    });
  }

  autoTable(doc, {
    startY: yPosition,
    head: [orgChartHeaders],
    body: orgChartData,
    theme: 'grid',
    headStyles: {
      fillColor: [219, 216, 216],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center'
    },
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'top'
    },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 80, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 60, halign: 'left' },
      2: { cellWidth: 50, halign: 'left' }
    } : {
      0: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 90, halign: 'left' }
    },
    margin: { left: margin, right: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Proficiency Testing Documents Section - Matching UI table structure
  checkAndAddPage(30);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.text('Proficiency Testing Documents', margin + 2, yPosition + 5);
  yPosition += 12;

  // Create table data matching the UI structure exactly
  let ptDocData = [];
      
      if (labDetail.QMSFile) {
        try {
          const files = JSON.parse(labDetail.QMSFile);
          if (Array.isArray(files) && files.length > 0) {
            files.forEach((file, index) => {
              // Find matching discipline
              const disciplineMatch = discipline.find(
                (d) =>
                  parseInt(d.DisciplineId) === parseInt(file.disciplineId) &&
                  parseInt(d.GroupId) === parseInt(file.groupId)
              );
              
              const disciplineName = disciplineMatch?.DisciplineName || "";
              const groupName = disciplineMatch?.groupName || "";
              const displayName = `${disciplineName}${disciplineName && groupName ? ' - ' : ''}${groupName}`;
              
              ptDocData.push([
                index === 0 ? 'Proficiency Testing Documents' : '',
                displayName ? `${displayName}: Available` : 'Available'
              ]);
            });
          } else {
            ptDocData.push([
              'Proficiency Testing Documents',
              'Available'
            ]);
          }
        } catch (error) {
          console.error("Error parsing QMSFile in PDF:", error);
          ptDocData.push([
            'Proficiency Testing Documents',
            'Available'
          ]);
        }
      } else {
        ptDocData.push([
          'Proficiency Testing Documents',
          '-'
        ]);
      }

  const ptDocHeaders = isObservationVisible
    ? ['Document Type', 'Status', 'Observations']
    : ['Document Type', 'Status'];

  // Add observations column if needed
  if (isObservationVisible) {
    ptDocData.forEach(row => {
      const ptDocObservations = observations.filter(obs =>
        obs.SectionName?.toLowerCase() === "proficiency testing document"
      );
      const observationText = ptDocObservations.length > 0 ?
        (ptDocObservations[0].ObservationRemarks || ptDocObservations[0].Remarks || ptDocObservations[0].Observation || '-') : '-';
      row.push(observationText);
    });
  }

  autoTable(doc, {
    startY: yPosition,
    head: [ptDocHeaders],
    body: ptDocData,
    theme: 'grid',
    headStyles: {
      fillColor: [219, 216, 216],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center'
    },
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'top'
    },
    columnStyles: isObservationVisible ? {
      0: { cellWidth: 80, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 60, halign: 'left' },
      2: { cellWidth: 50, halign: 'left' }
    } : {
      0: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
      1: { cellWidth: 90, halign: 'left' }
    },
    margin: { left: margin, right: margin },
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Declaration Section
  checkAndAddPage(50);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(227, 242, 253);
  doc.rect(margin, yPosition, contentWidth, 10, 'F');
  doc.text('DECLARATION', pageWidth / 2, yPosition + 6, { align: 'center' });
  yPosition += 15;

  // Terms & Conditions Section
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Terms & Conditions', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');

  const termsText = `The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.

The Conformity Assessment Body {Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:

By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.

Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.`;

  const termsLines = doc.splitTextToSize(termsText, contentWidth);
  doc.text(termsLines, margin, yPosition);
  yPosition += termsLines.length * 4 + 10;

  // Declaration by the Laboratory Section
  checkAndAddPage(40);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Declaration by the Laboratory', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('We declare that:', margin, yPosition);
  yPosition += 6;

  const declarationPoints = [
    'We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.',
    'We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).',
    'We have declared all sources of samples.',
    'We have declared and submitted accurate and complete information in the enclosed "Declaration Form"',
    'We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.',
    'We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).',
    'We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.',
    'We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.',
    'We comply to all national, regional and local regulatory requirements for operating a laboratory.',
    'All information provided in this application is true.',
    'Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO 15189.'
  ];

  declarationPoints.forEach((point, index) => {
    checkAndAddPage(15);
    const pointText = `${index + 1}. ${point}`;
    const pointLines = doc.splitTextToSize(pointText, contentWidth - 5);
    doc.text(pointLines, margin, yPosition);
    yPosition += pointLines.length * 4 + 3;
  });

  yPosition += 10;

  // Add additional declaration text
  checkAndAddPage(20);
  const additionalText1 = `${labDetail.DeclNameOrg || 'The authorized representative of CAB'}, authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.`;
  const additionalLines1 = doc.splitTextToSize(additionalText1, contentWidth);
  doc.text(additionalLines1, margin, yPosition);
  yPosition += additionalLines1.length * 4 + 8;

  checkAndAddPage(20);
  const additionalText2 = `I have read and understood all the terms and conditions mentioned above. *`;
  const additionalLines2 = doc.splitTextToSize(additionalText2, contentWidth);
  doc.text(additionalLines2, margin, yPosition);
  yPosition += additionalLines2.length * 4 + 8;

  checkAndAddPage(30);
  const additionalText3 = `By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'. *`;
  const additionalLines3 = doc.splitTextToSize(additionalText3, contentWidth);
  doc.text(additionalLines3, margin, yPosition);
  yPosition += additionalLines3.length * 4 + 10;

  // Add footer with page numbers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth - margin,
      pageHeight - 10,
      { align: 'right' }
    );
  }

  return doc;
};

export default {
  generateCompletePreviewReportPDF,
  generateCompletePreviewReportPDFCalibration
};
